import { listAllPrompts } from '@/lib/listAllPrompts';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactElement,
} from 'react';
import { Link } from 'react-router-dom';
import {
  v2,
  ApiError,
  type PromptLibraryEntry,
  type PromptWriteBody,
  type PromptTestResponse,
  type PromptClassifierTestResult,
  type SettingSourceName,
  type Submission,
  type ScanLearnJob,
  type GeneratingCompanyEntry,
  type ClassificationEvidenceEntry,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { isPromptActive } from '@/lib/promptActive';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { useSetting } from '@/hooks/useSetting';
import { Spinner } from '@/components/dashboard/Spinner';
import { Pagination } from '@/components/dashboard/Pagination';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { MovableDialog } from '@/components/shared/MovableDialog';
import { PageToast } from '@/components/shared/PageToast';
import { useUiToast } from '@/hooks/useUiToast';
// formatDate removed in Phase fix-unified-table (no "Updated" column in new table)
import { PermissionDenied } from '@/pages/PermissionDenied';

const PAGE_SIZE = 50;
/** Tracker row 6 — submissions offered by the classifier test's "as if in" picker (the list route caps at 200). */
const SUBMISSION_PICKER_LIMIT = 200;

// Region values accepted by the Phase 5.11 backend (see src/routes/prompts.ts
// VALID_REGIONS).
const REGIONS = ['all', 'FDA', 'EMA', 'HC', 'PMDA', 'TGA', 'MHRA'] as const;
const MODULES = [1, 2, 3, 4, 5] as const;

// Phase prompt-library-test-sandbox (TODO-076) — list filter constants.
// These values match the Region column in the PromptLibrary table.
const LIST_FILTER_REGIONS = ['FDA', 'HC', 'EMA', 'MHRA', 'PMDA', 'TGA', 'Swissmedic'] as const;

// Backend (src/routes/prompts.ts) emits `{ error, message? }` for 4xx.
// ValidationError codes that ship with a `message` (e.g. "title must
// be 1..200 chars") fall through to `parsed.message`; bare codes
// (NotFound, AuthRequired) get a friendlier rewrite via the map below.
// Phase E-polish-extract hoisted the formatXxxError helper to
// `@/lib/errors`; this map is passed in as the `codeMap` argument.
const PROMPT_LIBRARY_ERROR_MESSAGES: Record<string, string> = {
  NotFound: 'This prompt no longer exists. The list may be out of date.',
  AuthRequired: 'Your session expired. Please sign in again.',
  // T-17 (T17-D18) — Scan & Learn folder confinement (SCAN_LEARN_ROOT).
  ScanFolderRootNotConfigured:
    'Folder scans are disabled: no scan root is configured on the server. Ask an administrator to set SCAN_LEARN_ROOT.',
  ScanFolderOutsideRoot: 'That folder is outside the folder scans are allowed to read. Choose a folder inside the scan root.',
  ScanFolderNotFound: 'That folder does not exist on the server (inside the scan root).',
  ScanFolderInvalid: 'That is not a valid folder path.',
};

interface FormDraft {
  Region: string;
  Module: string; // string in the form, parsed to int|null on submit
  Section: string;
  ScopeType: string;
  ScopeId: string;
  Title: string;
  PromptText: string;
  // Phase 5.X-prompt-library-expansion — free-form classifier
  // category. Surfaces in the Type column.
  Type: string;
  // Phase 8.3 — Layer 2 classification fields.
  PromptType: 'Template' | 'Classification';
  IsActive: boolean;
  FilenamePatterns: string; // raw JSON or comma-separated globs
  GeneratingCompany: string;
  GeneratingCompanyLocation: string;
  DocumentType: string;
}

const EMPTY_DRAFT: FormDraft = {
  Region: 'all',
  Module: '',
  Section: '',
  ScopeType: '',
  ScopeId: '',
  Title: '',
  PromptText: '',
  Type: '',
  PromptType: 'Template',
  IsActive: true,
  FilenamePatterns: '',
  GeneratingCompany: '',
  GeneratingCompanyLocation: '',
  DocumentType: '',
};

function entryToDraft(p: PromptLibraryEntry): FormDraft {
  return {
    Region: p.Region,
    Module: p.Module === null ? '' : String(p.Module),
    Section: p.Section ?? '',
    ScopeType: p.ScopeType ?? '',
    ScopeId: p.ScopeId ?? '',
    Title: p.Title,
    PromptText: p.PromptText,
    Type: p.Type ?? '',
    PromptType: (p.PromptType === 'Classification' ? 'Classification' : 'Template'),
    IsActive: p.IsActive ?? true,
    FilenamePatterns: p.FilenamePatterns ?? '',
    GeneratingCompany: p.GeneratingCompany ?? '',
    GeneratingCompanyLocation: p.GeneratingCompanyLocation ?? '',
    DocumentType: p.DocumentType ?? '',
  };
}

function draftToBody(draft: FormDraft): PromptWriteBody {
  const body: PromptWriteBody = {
    region: draft.Region,
    title: draft.Title,
    promptText: draft.PromptText,
  };
  if (draft.Module !== '') {
    const n = Number.parseInt(draft.Module, 10);
    if (Number.isFinite(n)) body.module = n;
  } else {
    body.module = null;
  }
  body.section = draft.Section === '' ? null : draft.Section;
  body.scopeType = draft.ScopeType === '' ? null : draft.ScopeType;
  body.scopeId = draft.ScopeId === '' ? null : draft.ScopeId;
  body.type = draft.Type.trim() === '' ? null : draft.Type.trim();
  // Phase 8.3 — Layer 2 fields.
  body.promptType = draft.PromptType;
  body.isActive = draft.IsActive;
  body.filenamePatterns = draft.FilenamePatterns.trim() === '' ? null : draft.FilenamePatterns.trim();
  body.generatingCompany = draft.GeneratingCompany.trim() === '' ? null : draft.GeneratingCompany.trim();
  body.generatingCompanyLocation = draft.GeneratingCompanyLocation.trim() === '' ? null : draft.GeneratingCompanyLocation.trim();
  body.documentType = draft.DocumentType.trim() === '' ? null : draft.DocumentType.trim();
  return body;
}

interface ModalState {
  readonly mode: 'create' | 'edit';
  readonly entry: PromptLibraryEntry | null;
}

// Phase E-polish-extract-3 — inlined ConfirmModal hoisted to
// `@/components/shared/ConfirmModal`.

const tdStyle: CSSProperties = { padding: '12px 8px', verticalAlign: 'top' };

export function PromptLibraryPage(): ReactElement {
  const perms = useUserPermissions();
  const canRead = perms.hasPermission('Prompts', 'read');
  const canWrite = perms.hasPermission('Prompts', 'write');
  const canDelete = perms.hasPermission('Prompts', 'delete');

  const [prompts, setPrompts] = useState<PromptLibraryEntry[] | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // phase-fix-prompt-library-redesign — filter row replaces the prior
  // Region / Module / Section / Type combination with the four filters
  // specified in §3 of the redesign prompt (Type / Company / Status /
  // Search). Filters apply to Layer 2 rows only; L1 + L3 rows are always
  // rendered. Backend list query receives only `promptType` (the Type
  // filter) — Company / Status / Search are applied client-side over the
  // backend page so a single backend query serves the visible rows.
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [companyFilter, setCompanyFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Phase prompt-library-test-sandbox (TODO-076) — Region + Module filters.
  const [regionFilter, setRegionFilter] = useState<string>('All');
  const [moduleFilter, setModuleFilter] = useState<string>('All');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PromptLibraryEntry | null>(null);
  // phase-prompt-library-honest-controls item 6 — Set All Active is a bulk
  // status change; it must say exactly what it will do before doing it.
  // Set All Active acts on exactly the prompts the user sees: the filtered
  // Layer 2 list is snapshotted when the dialog opens, counted in the dialog,
  // and sent as-is — so dialog, request and toast are about the same rows.
  const [setAllActiveTargets, setSetAllActiveTargets] = useState<PromptLibraryEntry[] | null>(null);
  // Phase fix-3-layer — Scan & Learn modal state.
  const [scanModalOpen, setScanModalOpen] = useState(false);
  // Tracker row 11 — a scan found on arrival that the dialog is not showing.
  const [scanBanner, setScanBanner] = useState<ScanLearnJob | null>(null);
  // ISS-009 — Layer 3 rows must describe the EFFECTIVE classification
  // settings, resolved through the canonical settings hook (schema defaults
  // applied when no row exists), not a forked fetch with a hardcoded 85.
  const autoConfirm = useSetting<boolean>('autoConfirmHighConfidence');
  const confidenceThreshold = useSetting<number>('confidenceThreshold');
  const reviewThresholdSetting = useSetting<number>('reviewConfidenceThreshold');
  // Phase fix-3-layer — Classification prompts for the grouped Layer 2 view.
  const [classificationPrompts, setClassificationPrompts] = useState<PromptLibraryEntry[] | null>(null);
  // Phase fix-3-layer — pending companies count for banner.
  const [pendingCompaniesCount, setPendingCompaniesCount] = useState(0);
  const [pendingCountError, setPendingCountError] = useState<string | null>(null);
  // phase-classification-stage-1-filename-layer — real Layer 1 evidence.
  const [evidenceEntries, setEvidenceEntries] = useState<ClassificationEvidenceEntry[] | null>(null);
  const { toast, showToast, clearToast } = useUiToast();
  // Ref for the PendingCompaniesPanel scroll target.
  const pendingPanelRef = useRef<HTMLElement>(null);

  // Reset to page 1 when filters change.
  useEffect(() => setPage(1), [typeFilter, companyFilter, statusFilter, searchQuery, regionFilter, moduleFilter]);

  useEffect(() => {
    if (!canRead) return;
    setPrompts(null);
    setError(null);
    let stale = false;
    const q: Parameters<typeof v2.prompts.list>[0] = {};
    // phase-fix-prompt-library-redesign — only Type goes to backend;
    // Company / Status / Search are applied client-side below.
    if (typeFilter === 'Template' || typeFilter === 'Classification') {
      q.promptType = typeFilter;
    }
    listAllPrompts(q, () => stale)
      .then((rows) => { if (!stale) setPrompts(rows); })
      .catch((err: unknown) => {
        if (stale) return;
        setError(formatApiError(err, 'Failed to load prompt library', PROMPT_LIBRARY_ERROR_MESSAGES));
        setPrompts([]);
      });
    return () => { stale = true; };
  }, [canRead, typeFilter, reloadKey]);

  // Phase fix-3-layer — fetch Classification-type prompts for the grouped Layer 2 view.
  useEffect(() => {
    if (!canRead) return;
    listAllPrompts({ promptType: 'Classification' })
      .then(setClassificationPrompts)
      .catch(() => setClassificationPrompts([]));
  }, [canRead, reloadKey]);

  // Phase fix-3-layer — fetch pending companies count for banner.
  // T16-D4 — GET /generating-companies requires Prompts:write, so the count is
  // requested (and shown) only for users who hold it; a read-only user would
  // only ever get a 403. A failed load says so — it is never a silent zero.
  useEffect(() => {
    if (!canWrite) return;
    v2.generatingCompanies
      .list({ status: 'pending', pageSize: 1 })
      .then((res) => {
        setPendingCompaniesCount(res.total);
        setPendingCountError(null);
      })
      .catch(() => {
        setPendingCompaniesCount(0);
        setPendingCountError('Could not load the number of companies awaiting review.');
      });
  }, [canWrite, reloadKey]);

  // Tracker row 11 — a scan keeps running server-side when the admin leaves
  // this page. On return: the scan THIS tab started (sessionStorage hint)
  // re-opens in the progress view, or shows its outcome once if it finished
  // meanwhile; any other running or unseen job is announced in a banner.
  useEffect(() => {
    if (!canWrite) return;
    let live = true;
    v2.scanLearn
      .latest()
      .then((latest) => {
        if (!live || !latest) return;
        const mine = sameJobId(readScanJobHint(), latest.Id);
        if (isScanJobRunning(latest) || (isScanJobFinished(latest) && !latest.outcomeAcknowledged)) {
          if (mine) {
            setScanModalOpen(true); // the dialog resumes it (and shows the outcome once)
          } else {
            setScanBanner(latest);
            if (isScanJobFinished(latest)) acknowledgeScanOutcome(latest);
          }
        }
      })
      .catch(() => {
        // Not knowing the latest job hides nothing: the dialog looks again when opened.
      });
    return () => {
      live = false;
    };
  }, [canWrite]);

  // phase-classification-stage-1-filename-layer — fetch the real Layer 1
  // pattern evidence (confirmed-history + structure-only scan feeders).
  useEffect(() => {
    if (!canRead) return;
    v2.classificationEvidence
      .list()
      .then((res) => setEvidenceEntries(res.entries))
      .catch(() => setEvidenceEntries([]));
  }, [canRead, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const handleDeleteConfirmed = async (p: PromptLibraryEntry): Promise<void> => {
    setDeleteTarget(null);
    try {
      await v2.prompts.delete(p.Id);
      reload();
      showToast({ type: 'success', text: `Deleted prompt "${p.Title}".` });
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to delete prompt', PROMPT_LIBRARY_ERROR_MESSAGES),
      });
    }
  };

  // Phase 5.X-prompt-library-expansion handlers.
  const handleToggleStatus = async (p: PromptLibraryEntry): Promise<void> => {
    // phase-ui-review-F14 — flip from the runtime-authoritative IsActive (via
    // the shared evaluator), not the legacy Status column the UI used to read.
    const next = isPromptActive(p) ? 'draft' : 'active';
    try {
      await v2.prompts.setStatus(p.Id, next);
      reload();
      showToast({
        type: 'success',
        text: `"${p.Title}" is now ${next}.`,
      });
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to update status', PROMPT_LIBRARY_ERROR_MESSAGES),
      });
    }
  };

  const handleDuplicate = async (p: PromptLibraryEntry): Promise<void> => {
    try {
      const created = await v2.prompts.duplicate(p.Id);
      reload();
      showToast({
        type: 'success',
        text: `Duplicated as "${created.Title}".`,
      });
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to duplicate prompt', PROMPT_LIBRARY_ERROR_MESSAGES),
      });
    }
  };

  // phase-prompt-library-honest-controls item 6 — runs only after the admin
  // confirms in the ConfirmModal below. Sends exactly the prompts the dialog
  // counted; the toast states the server's real counts (and says so if they
  // differ from what the dialog promised) and offers Undo for what changed.
  const handleUndoBulkActivate = async (batchId: string): Promise<void> => {
    try {
      const res = await v2.prompts.revertBulkActivate(batchId);
      reload();
      const skipped =
        res.skipped > 0
          ? ` ${res.skipped} ${res.skipped === 1 ? 'was' : 'were'} changed by someone since and left as they are: ${res.skippedPrompts.map((s) => `"${s.title}"`).join(', ')}.`
          : '';
      showToast({
        type: 'success',
        text: `Undone: ${res.reverted} prompt${res.reverted === 1 ? '' : 's'} back to ${res.reverted === 1 ? 'its' : 'their'} previous status.${skipped}`,
      });
    } catch (err) {
      showToast({ type: 'error', text: formatApiError(err, 'Failed to undo', PROMPT_LIBRARY_ERROR_MESSAGES) });
    }
  };

  const handleSetAllActive = async (targets: PromptLibraryEntry[]): Promise<void> => {
    setSetAllActiveTargets(null);
    if (targets.length === 0) return;
    const promised = targets.filter((p) => !isPromptActive(p)).length;
    try {
      const res = await v2.prompts.bulkActivate(targets.map((p) => p.Id));
      reload();
      const parts = [`Set ${res.updated} prompt${res.updated === 1 ? '' : 's'} to active.`];
      if (res.alreadyActive > 0) parts.push(`${res.alreadyActive} ${res.alreadyActive === 1 ? 'was' : 'were'} already active.`);
      if (res.notFound > 0) parts.push(`${res.notFound} could not be found and ${res.notFound === 1 ? 'was' : 'were'} not changed.`);
      if (res.updated !== promised) {
        parts.push(`(The dialog expected ${promised}; the list changed after it was loaded.)`);
      }
      showToast({
        type: 'success',
        text: parts.join(' '),
        ...(res.updated > 0
          ? { action: { label: 'Undo', onClick: () => void handleUndoBulkActivate(res.batchId) } }
          : {}),
      });
    } catch (err) {
      showToast({
        type: 'error',
        text: formatApiError(err, 'Failed to set all active', PROMPT_LIBRARY_ERROR_MESSAGES),
      });
    }
  };

  const scrollToPendingPanel = (): void => {
    pendingPanelRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (perms.loading) return <Spinner caption="Checking permissions…" />;
  if (!canRead) return <PermissionDenied />;



  // phase-fix-prompt-library-redesign — Layer 2 client-side filters.
  // typeFilter is server-applied (see useEffect above); company / status /
  // search apply here so the backend pagination still matches the
  // Type-filtered slice.
  const filteredLayer2Prompts: PromptLibraryEntry[] = (prompts ?? []).filter((entry) => {
    if (companyFilter !== 'All' && (entry.GeneratingCompany ?? '') !== companyFilter) return false;
    // phase-ui-review-F14 — the Status dropdown offers 'active' / 'draft';
    // match against the runtime-authoritative IsActive via the shared evaluator.
    if (statusFilter !== 'All' && isPromptActive(entry) !== (statusFilter === 'active')) return false;
    // Phase prompt-library-test-sandbox (TODO-076) — Region + Module client-side filters.
    if (regionFilter !== 'All' && entry.Region !== regionFilter) return false;
    if (moduleFilter !== 'All') {
      const mod = Number.parseInt(moduleFilter.replace('M', ''), 10);
      if (entry.Module !== mod) return false;
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const hay = [
        entry.Title,
        entry.PromptText,
        entry.Section ?? '',
        entry.GeneratingCompany ?? '',
        entry.DocumentType ?? '',
        entry.Region,
      ]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const total = filteredLayer2Prompts.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const visibleLayer2Prompts = filteredLayer2Prompts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasActiveLayer2Filter =
    typeFilter !== 'All' ||
    companyFilter !== 'All' ||
    statusFilter !== 'All' ||
    regionFilter !== 'All' ||
    moduleFilter !== 'All' ||
    searchQuery.trim() !== '';

  // Company options for the filter dropdown — derived from the Classification
  // prompt set since L2 is the only layer that carries GeneratingCompany.
  const companyOptions = Array.from(
    new Set(
      (classificationPrompts ?? [])
        .map((p) => p.GeneratingCompany)
        .filter((c): c is string => Boolean(c && c.trim())),
    ),
  ).sort((a, b) => a.localeCompare(b));

  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22 }}>Prompt Library</h1>
        {canWrite && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setSetAllActiveTargets(visibleLayer2Prompts)}
              disabled={visibleLayer2Prompts.length === 0}
              style={toolbarSecondaryStyle}
              data-testid="prompts-set-all-active-btn"
            >
              Set All Active
            </button>
            <button
              type="button"
              onClick={() => setModal({ mode: 'create', entry: null })}
              data-testid="page-header-add-prompt-btn"
              style={{
                background: 'var(--accent)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + Add Prompt
            </button>
          </div>
        )}
      </div>

      {/* Tracker row 11 — a scan that is running, or finished while nobody
          was looking, found on arrival. Shown once; the server records it. */}
      {scanBanner && (
        <div
          data-testid="scan-learn-banner"
          role={scanBanner.Status === 'failed' ? 'alert' : 'status'}
          style={{
            background: scanBanner.Status === 'failed' ? '#FEF2F2' : 'var(--surface2, #F3F4F6)',
            border: `1px solid ${scanBanner.Status === 'failed' ? '#FCA5A5' : 'var(--border)'}`,
            color: scanBanner.Status === 'failed' ? '#991B1B' : 'var(--text)',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 12,
            fontSize: 13,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <span>
            {isScanJobRunning(scanBanner)
              ? `A scan${scanBanner.CreatedBy ? ` started by ${scanBanner.CreatedBy}` : ''} is in progress (${scanBanner.FilesProcessed ?? 0} / ${scanBanner.FilesFound ?? '?'} files when last checked).`
              : scanBanner.Status === 'failed'
                ? `Scan failed — nothing was applied.${scanBanner.FailureReason ? ` ${scanBanner.FailureReason}` : ''}`
                : `The last scan${scanBanner.CreatedBy ? ` (by ${scanBanner.CreatedBy})` : ''} finished. ${scanCompleteSummary(scanBanner)}`}
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            {isScanJobRunning(scanBanner) && (
              <button
                type="button"
                data-testid="scan-learn-banner-view"
                onClick={() => setScanModalOpen(true)}
                style={toolbarSecondaryStyle}
              >
                View progress
              </button>
            )}
            <button
              type="button"
              data-testid="scan-learn-banner-dismiss"
              onClick={() => setScanBanner(null)}
              style={toolbarSecondaryStyle}
            >
              Dismiss
            </button>
          </span>
        </div>
      )}

      {/* phase-fix-prompt-library-redesign — pending companies banner.
          Yellow/amber alert above the table; absent when count is zero.
          Replaces the same banner that used to live inside the deleted
          ClassificationLayersPanel. */}
      {pendingCountError && (
        <div
          data-testid="pending-companies-error"
          role="alert"
          style={{
            background: '#FEF2F2',
            border: '1px solid #FCA5A5',
            color: '#991B1B',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {pendingCountError}
        </div>
      )}
      {pendingCompaniesCount > 0 && (
        <div
          data-testid="pending-companies-banner"
          style={{
            background: '#FEF3C7',
            border: '1px solid #F59E0B',
            color: '#92400E',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 12,
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span>
            ⚠ <strong>{pendingCompaniesCount}</strong>{' '}
            {pendingCompaniesCount === 1 ? 'company is' : 'companies are'} awaiting review after
            the last Scan & Learn run.
          </span>
          <button
            type="button"
            onClick={scrollToPendingPanel}
            data-testid="pending-companies-review-btn"
            style={{
              background: 'none',
              border: '1px solid #F59E0B',
              color: '#92400E',
              borderRadius: 4,
              padding: '4px 10px',
              fontSize: 12,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Review →
          </button>
        </div>
      )}

      {/* phase-fix-prompt-library-redesign — filter row. Type / Company /
          Status / Search per §3. Applies to Layer 2 rows only; Layer 1
          and Layer 3 rows remain visible regardless. */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <label style={{ fontSize: 12 }}>
          Type
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Type filter"
            style={selectStyle}
          >
            <option value="All">All</option>
            <option value="Template">Template</option>
            <option value="Classification">Classification</option>
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          Company
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            aria-label="Company filter"
            style={selectStyle}
          >
            <option value="All">All</option>
            {companyOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Status filter"
            style={selectStyle}
          >
            <option value="All">All</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
          </select>
        </label>
        {/* Phase prompt-library-test-sandbox (TODO-076) — Region + Module list filters. */}
        <label style={{ fontSize: 12 }}>
          Region
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            aria-label="Region filter"
            style={selectStyle}
            data-testid="region-filter-select"
          >
            <option value="All">All</option>
            {LIST_FILTER_REGIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          Module
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            aria-label="Module filter"
            style={selectStyle}
            data-testid="module-filter-select"
          >
            <option value="All">All</option>
            {MODULES.map((m) => (
              <option key={m} value={`M${m}`}>
                M{m}
              </option>
            ))}
          </select>
        </label>
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search prompts…"
          aria-label="Search prompts"
          style={{
            ...selectStyle,
            marginLeft: 0,
            minWidth: 200,
          }}
        />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {visibleLayer2Prompts.length} of {total} Layer 2 prompt{total === 1 ? '' : 's'}
        </span>
      </div>

      {error && (
        <div role="alert" style={{ color: 'var(--red)', marginBottom: 12 }}>
          {error}
          <button
            type="button"
            onClick={reload}
            style={{ marginLeft: 12, padding: '4px 12px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* phase-fix-prompt-library-redesign — single unified 3-layer
          classification table. Replaces the old ClassificationLayersPanel
          + UnifiedClassificationTable pair. Never blank — Layer 3 rows
          (system settings) are always rendered; placeholder rows cover
          Layers 1 and 2 when empty. */}
      <UnifiedClassificationTable
        evidenceEntries={evidenceEntries ?? []}
        layer2Prompts={prompts === null ? null : visibleLayer2Prompts}
        layer2Total={total}
        layer2HasActiveFilter={hasActiveLayer2Filter}
        autoConfirmEnabled={autoConfirm.value}
        autoConfirmThreshold={confidenceThreshold.value}
        reviewThreshold={reviewThresholdSetting.value}
        canWrite={canWrite}
        canDelete={canDelete}
        onEditPrompt={(p) => setModal({ mode: 'edit', entry: p })}
        onDeletePrompt={(p) => setDeleteTarget(p)}
        onToggleActive={handleToggleStatus}
        onDuplicate={handleDuplicate}
        onCreatePrompt={() => setModal({ mode: 'create', entry: null })}
        onScanLearnOpen={() => setScanModalOpen(true)}
        onClearFilters={() => {
          setTypeFilter('All');
          setCompanyFilter('All');
          setStatusFilter('All');
          setRegionFilter('All');
          setModuleFilter('All');
          setSearchQuery('');
        }}
      />
      {prompts !== null && prompts.length > 0 && (
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      {modal && (
        <PromptEditModal
          mode={modal.mode}
          entry={modal.entry}
          onClose={() => setModal(null)}
          onSaved={(savedTitle) => {
            const wasCreate = modal.mode === 'create';
            setModal(null);
            reload();
            showToast({
              type: 'success',
              text: wasCreate
                ? `Created prompt "${savedTitle}".`
                : `Saved changes to "${savedTitle}".`,
            });
          }}
        />
      )}
      {/* phase-prompt-library-honest-controls item 6 — Set All Active names
          exactly what will change (and how many rows) before it acts. */}
      {setAllActiveTargets && setAllActiveTargets.length > 0 && (
        <ConfirmModal
          title="Set all prompts active"
          message={(() => {
            const totalCount = setAllActiveTargets.length;
            const willChange = setAllActiveTargets.filter((p) => !isPromptActive(p)).length;
            const already = totalCount - willChange;
            if (willChange === 0) {
              return totalCount === 1
                ? 'The 1 prompt listed here is already active — confirming will change nothing.'
                : `All ${totalCount} prompts listed here are already active — confirming will change nothing.`;
            }
            return `${willChange} prompt${willChange === 1 ? '' : 's'} will change to Active, ${already} already active (${totalCount} listed here${hasActiveLayer2Filter ? ', with the current filters' : ''}). You can undo this from the confirmation message.`;
          })()}
          confirmLabel="Set All Active"
          onCancel={() => setSetAllActiveTargets(null)}
          onConfirm={() => void handleSetAllActive(setAllActiveTargets)}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          title="Delete prompt"
          message={`Permanently delete "${deleteTarget.Title}"? This cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirmed(deleteTarget)}
        />
      )}
      {/* Phase 8.4 — Pending GeneratingCompanies panel (scroll target) */}
      {canWrite && (
        <PendingCompaniesPanel
          ref={pendingPanelRef}
          onCompaniesReviewed={reload}
        />
      )}

      {scanModalOpen && (
        <ScanAndLearnModal
          onClose={() => setScanModalOpen(false)}
          onJobUpdate={(job) => {
            // The dialog now shows this job: keep the banner in step with it.
            setScanBanner((b) => (b && sameJobId(b.Id, job.Id) ? (isScanJobRunning(job) ? job : null) : b));
          }}
          onComplete={(job) => {
            // The dialog stays open with the full summary; the toast carries the
            // same true counts (tracker row 8).
            reload();
            showToast({ type: 'success', text: scanCompleteSummary(job) });
          }}
        />
      )}
      <PageToast toast={toast} onDismiss={clearToast} />
    </section>
  );
}


const selectStyle: CSSProperties = {
  marginLeft: 8,
  padding: '6px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
};

// Phase 5.X-prompt-library-expansion — secondary toolbar buttons
// (Set All Active, Scan & Learn) share the V1 outline-button style.
const toolbarSecondaryStyle: CSSProperties = {
  background: 'none',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '6px 12px',
  fontSize: 12,
  cursor: 'pointer',
};

interface ModalProps {
  readonly mode: 'create' | 'edit';
  readonly entry: PromptLibraryEntry | null;
  readonly onClose: () => void;
  readonly onSaved: (savedTitle: string) => void;
}

function PromptEditModal({ mode, entry, onClose, onSaved }: ModalProps): ReactElement {
  // Tracker row 14 — testing a prompt is a billed model call and the server
  // requires Prompts.write for it (POST /prompts/:id/test), like every other
  // write action here. The dialog is only reachable through write-gated
  // buttons today; the Test panel checks the permission itself as well.
  const canTest = useUserPermissions().hasPermission('Prompts', 'write');
  const [draft, setDraft] = useState<FormDraft>(() =>
    entry ? entryToDraft(entry) : EMPTY_DRAFT,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (k: keyof FormDraft, v: string): void =>
    setDraft((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!draft.Title.trim()) {
      setError('Title is required');
      return;
    }
    if (draft.Title.length > 200) {
      setError('Title must be ≤ 200 characters');
      return;
    }
    if (!draft.PromptText.trim()) {
      setError('Prompt text is required');
      return;
    }
    if (draft.Section && draft.Section.length > 64) {
      setError('Section must be ≤ 64 characters');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const body = draftToBody(draft);
      if (mode === 'create') {
        await v2.prompts.create(body);
      } else if (entry) {
        await v2.prompts.update(entry.Id, body);
      }
      onSaved(draft.Title);
    } catch (err) {
      setError(formatApiError(err, 'Save failed', PROMPT_LIBRARY_ERROR_MESSAGES));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid var(--border)',
    borderRadius: 6,
    fontSize: 13,
  };

  // phase-movable-dialogs — Sharon's concrete obstruction: this dialog
  // covered the very row being edited, and the test panel covered further
  // rows. Now the ONE shared movable shell: drag by the title bar to see the
  // table behind; resizable (the prompt textarea is the clear beneficiary);
  // remembered position; Escape closes; backdrop click still closes (the
  // pre-conversion behaviour).
  return (
    <MovableDialog
      persistKey="promptLibrary.editor"
      title={mode === 'create' ? 'New prompt' : `Edit "${entry?.Title}"`}
      ariaLabel={mode === 'create' ? 'New prompt' : 'Edit prompt'}
      onClose={onClose}
      closeOnBackdrop
      resizable
      minSize={{ width: 560, height: 440 }}
      width={mode === 'edit' && entry ? 'min(1200px, 94vw)' : 'min(720px, 92vw)'}
      zIndex={300}
      headerRight={
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'none',
            border: 'none',
            fontSize: 20,
            cursor: 'pointer',
            color: 'var(--text-muted)',
          }}
        >
          ×
        </button>
      }
      bodyStyle={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}
    >
      <form
        onSubmit={handleSubmit}
        style={{ flex: '1 1 0', minWidth: 0 }}
      >

        {error && (
          <div
            role="alert"
            style={{
              color: 'var(--red)',
              background: 'var(--red-bg)',
              padding: '8px 12px',
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <Field label="Title *">
          <input
            type="text"
            value={draft.Title}
            onChange={(e) => update('Title', e.target.value)}
            required
            aria-label="Prompt title"
            style={inputStyle}
            maxLength={200}
          />
        </Field>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
          }}
        >
          <Field label="Region *">
            <select
              value={draft.Region}
              onChange={(e) => update('Region', e.target.value)}
              aria-label="Prompt region"
              required
              style={inputStyle}
            >
              {REGIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Module">
            <select
              value={draft.Module}
              onChange={(e) => update('Module', e.target.value)}
              aria-label="Prompt module"
              style={inputStyle}
            >
              <option value="">— Any —</option>
              {MODULES.map((m) => (
                <option key={m} value={String(m)}>
                  m{m}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Section">
            <input
              type="text"
              value={draft.Section}
              onChange={(e) => update('Section', e.target.value)}
              placeholder="e.g. 3.2.s.1"
              maxLength={64}
              aria-label="Prompt section"
              style={inputStyle}
            />
          </Field>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
          }}
        >
          {/* phase-prompt-library-honest-controls items 3+4 — Scope type and
              Scope id are free-text (the server enforces only length, no value
              list), and NOTHING reads them yet: they are saved with the prompt
              but no feature — including classification — consults them. Say so
              rather than letting the placeholder imply they steer anything. */}
          <Field
            label="Scope type"
            hint="Optional free-text label (up to 32 characters, e.g. sponsor or cro). Not used yet: it is saved with the prompt but nothing in the app reads it."
          >
            <input
              type="text"
              value={draft.ScopeType}
              onChange={(e) => update('ScopeType', e.target.value)}
              placeholder="sponsor, cro, …"
              maxLength={32}
              aria-label="Prompt scope type"
              style={inputStyle}
            />
          </Field>
          <Field
            label="Scope id"
            hint="Optional identifier to pair with Scope type (up to 255 characters). Not used yet: it is saved with the prompt but nothing in the app reads it."
          >
            <input
              type="text"
              value={draft.ScopeId}
              onChange={(e) => update('ScopeId', e.target.value)}
              placeholder="Optional id"
              maxLength={255}
              aria-label="Prompt scope id"
              style={inputStyle}
            />
          </Field>
          <Field
            label="Type"
            hint="Optional free-text category for your own organisation (up to 50 characters), e.g. cover-letter-generation. Any text is accepted."
          >
            <input
              type="text"
              value={draft.Type}
              onChange={(e) => update('Type', e.target.value)}
              placeholder="classification, summary, …"
              maxLength={50}
              aria-label="Prompt type"
              style={inputStyle}
            />
          </Field>
        </div>

        {/* Phase 8.3 — Layer 2 Classification fields */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            marginTop: 12,
          }}
        >
          <Field
            label="Prompt kind"
            hint="Template = a prompt that generates document content (like a cover letter). Classification (Layer 2) = a prompt the AI classifier uses to place documents; choosing it reveals the classification fields below. These are the only two kinds."
          >
            <select
              value={draft.PromptType}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  PromptType: e.target.value === 'Classification' ? 'Classification' : 'Template',
                }))
              }
              aria-label="Prompt kind"
              style={inputStyle}
            >
              <option value="Template">Template</option>
              <option value="Classification">Classification (Layer 2)</option>
            </select>
          </Field>
          <Field label="Active for classification">
            <select
              value={draft.IsActive ? 'true' : 'false'}
              onChange={(e) =>
                setDraft((d) => ({ ...d, IsActive: e.target.value === 'true' }))
              }
              aria-label="Is active"
              style={inputStyle}
              disabled={draft.PromptType !== 'Classification'}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </Field>
        </div>

        {draft.PromptType === 'Classification' && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 12,
                marginTop: 12,
              }}
            >
              <Field label="Generating company">
                <input
                  type="text"
                  value={draft.GeneratingCompany}
                  onChange={(e) => update('GeneratingCompany', e.target.value)}
                  placeholder="e.g. Pfizer"
                  maxLength={256}
                  aria-label="Generating company"
                  style={inputStyle}
                />
              </Field>
              <Field label="Company location">
                <input
                  type="text"
                  value={draft.GeneratingCompanyLocation}
                  onChange={(e) => update('GeneratingCompanyLocation', e.target.value)}
                  placeholder="e.g. USA"
                  maxLength={256}
                  aria-label="Generating company location"
                  style={inputStyle}
                />
              </Field>
              <Field label="Document type">
                <input
                  type="text"
                  value={draft.DocumentType}
                  onChange={(e) => update('DocumentType', e.target.value)}
                  placeholder="e.g. Clinical Study Report"
                  maxLength={128}
                  aria-label="Document type"
                  style={inputStyle}
                />
              </Field>
            </div>
            <Field label="Filename patterns (JSON glob array)">
              <div>
                <input
                  type="text"
                  value={draft.FilenamePatterns}
                  onChange={(e) => update('FilenamePatterns', e.target.value)}
                  placeholder='e.g. ["*.pdf","*clinical*"]'
                  maxLength={2000}
                  aria-label="Filename patterns"
                  style={inputStyle}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Leave blank to match any filename. Use glob syntax, e.g.{' '}
                  <code>{'["*.pdf","*clinical*"]'}</code>.
                </div>
              </div>
            </Field>
          </>
        )}

        <Field label="Prompt text *">
          <textarea
            value={draft.PromptText}
            onChange={(e) => update('PromptText', e.target.value)}
            required
            aria-label="Prompt text"
            rows={10}
            style={{
              ...inputStyle,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 12,
              resize: 'vertical',
            }}
          />
        </Field>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : mode === 'create' ? 'Create prompt' : 'Save changes'}
          </button>
        </div>
      </form>
      {/* Phase 5.X-prompt-library-expansion — V1 Test panel. Rides inside the
          movable window beside the form, so dragging the dialog moves both. */}
      {mode === 'edit' && entry && canTest && (
        <PromptTestPanel promptId={entry.Id} promptTitle={entry.Title} promptType={entry.PromptType} />
      )}
    </MovableDialog>
  );
}

// phase-prompt-library-honest-controls item 3 — `hint` renders a short
// plain-language explanation under the control for fields whose purpose
// isn't obvious from the label alone.
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactElement;
}): ReactElement {
  return (
    <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
      <span style={{ display: 'block', marginBottom: 4 }}>{label}</span>
      {children}
      {hint && (
        <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

// Phase fix-unified-table: PromptTypeChip and StatusBadge removed — their
// functionality is now inlined directly in UnifiedClassificationTable rows,
// preserving the data-testid="prompt-status-badge-{status}" testid pattern.

/**
 * V1's Test panel.
 *
 * Tracker row 6 — a Classification prompt is tested through the CLASSIFIER
 * (POST /prompt-library/test → the same classification decision a real
 * classify runs), with a sample file name because filename patterns and
 * Layer 1 depend on it. Any other prompt keeps the free-form model call,
 * labelled as such: it is not the classifier.
 */
function PromptTestPanel({
  promptId,
  promptTitle,
  promptType,
}: {
  promptId: string;
  promptTitle: string;
  promptType?: string | null;
}): ReactElement {
  const isClassification = promptType === 'Classification';
  const [sample, setSample] = useState('');
  const [sampleFileName, setSampleFileName] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<PromptTestResponse | null>(null);
  const [classifierResult, setClassifierResult] = useState<PromptClassifierTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Tracker row 6 — a real document is classified in ITS submission (region +
  // the submission's own settings). Choosing one makes the test exact; with
  // none, the server says how far the answer holds.
  const [submissionId, setSubmissionId] = useState('');
  const [submissions, setSubmissions] = useState<{ rows: Submission[]; total: number } | null>(null);
  const [submissionsError, setSubmissionsError] = useState<string | null>(null);

  useEffect(() => {
    if (!isClassification) return;
    let live = true;
    v2.submissions
      .list({ limit: SUBMISSION_PICKER_LIMIT })
      .then((r) => {
        if (live) setSubmissions({ rows: r.data, total: r.pagination.total });
      })
      .catch((err: unknown) => {
        if (live) setSubmissionsError(formatApiError(err, 'Could not load submissions', PROMPT_LIBRARY_ERROR_MESSAGES));
      });
    return () => {
      live = false;
    };
  }, [isClassification]);

  const runTest = async (): Promise<void> => {
    if (isClassification && !sampleFileName.trim()) {
      setError('Enter the sample file name — filename patterns and Layer 1 rules depend on it.');
      return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    setClassifierResult(null);
    try {
      if (isClassification) {
        setClassifierResult(
          await v2.prompts.testClassification({
            promptId,
            sampleFileName: sampleFileName.trim(),
            sampleContent: sample,
            ...(submissionId ? { submissionId } : {}),
          }),
        );
      } else {
        setResult(await v2.prompts.test(promptId, sample));
      }
    } catch (err) {
      setError(formatApiError(err, 'Test failed', PROMPT_LIBRARY_ERROR_MESSAGES));
    } finally {
      setRunning(false);
    }
  };

  return (
    <section
      data-testid="prompt-test-panel"
      style={{
        background: 'var(--surface2, var(--surface))',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: 16,
        flex: '1 1 0',
        minWidth: 280,
      }}
    >
      <header style={{ marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>Test prompt — {promptTitle}</strong>
        <div data-testid="prompt-test-mode" style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          {isClassification
            ? 'Classifier test — runs the same classification pipeline as a real document classify.'
            : 'Free-form test, not the classifier — the prompt text is sent to the model as-is.'}
        </div>
      </header>
      {isClassification && (
        <input
          type="text"
          value={sampleFileName}
          onChange={(e) => setSampleFileName(e.target.value)}
          aria-label="Sample file name for classifier test"
          data-testid="prompt-test-filename"
          placeholder="Sample file name, e.g. cover-letter.pdf"
          style={{
            width: '100%',
            padding: 8,
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 8,
            boxSizing: 'border-box',
          }}
        />
      )}
      {isClassification && (
        <div style={{ marginBottom: 8 }}>
          <select
            value={submissionId}
            onChange={(e) => setSubmissionId(e.target.value)}
            aria-label="Classify as if in submission"
            data-testid="prompt-test-submission"
            style={{
              width: '100%',
              padding: 8,
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 13,
              boxSizing: 'border-box',
              background: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            <option value="">No submission: your and the organisation's settings</option>
            {(submissions?.rows ?? []).map((s) => (
              <option key={s.Id} value={s.Id}>
                {`${s.DrugName} · seq ${s.SequenceNumber} · ${s.Region}`}
              </option>
            ))}
          </select>
          <div data-testid="prompt-test-submission-note" style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {submissionsError
              ? `${submissionsError} The test can still run without a submission.`
              : submissions === null
                ? 'Loading submissions…'
                : submissions.total > submissions.rows.length
                  ? `Showing the first ${submissions.rows.length} of ${submissions.total} submissions.`
                  : 'A real document is classified with its submission’s region and settings; choose one to test exactly.'}
          </div>
        </div>
      )}
      <textarea
        value={sample}
        onChange={(e) => setSample(e.target.value)}
        rows={4}
        aria-label="Sample document text for prompt test"
        placeholder="Paste sample document content here…"
        style={{
          width: '100%',
          padding: 10,
          border: '1px solid var(--border)',
          borderRadius: 6,
          fontSize: 13,
          resize: 'vertical',
          marginBottom: 8,
        }}
      />
      <button
        type="button"
        onClick={() => void runTest()}
        disabled={running}
        data-testid="prompt-test-run-btn"
        style={{
          background: 'var(--accent)',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          cursor: running ? 'wait' : 'pointer',
          opacity: running ? 0.6 : 1,
        }}
      >
        {running ? 'Running…' : isClassification ? 'Run classifier test' : 'Run Test'}
      </button>
      {error && (
        <div
          role="alert"
          style={{
            marginTop: 8,
            padding: '8px 12px',
            color: 'var(--red)',
            background: 'var(--red-bg, #fee2e2)',
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {result && (
        <div
          data-testid="prompt-test-output"
          style={{
            marginTop: 10,
            padding: 10,
            background: 'var(--surface2, #f3f4f6)',
            borderRadius: 6,
            fontSize: 12,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            whiteSpace: 'pre-wrap',
          }}
        >
          {/* phase-prompt-library-honest-controls item 1 — the result is a
              real model call now; show which model ran and what it consumed. */}
          <div style={{ marginBottom: 6, color: 'var(--text-muted)' }}>
            Model: {result.model} · Latency: {result.latencyMs} ms
            {result.tokensIn !== null && result.tokensOut !== null
              ? ` · Tokens: ${result.tokensIn} in / ${result.tokensOut} out`
              : ''}
          </div>
          {result.output}
        </div>
      )}
      {classifierResult && <ClassifierTestResult result={classifierResult} />}
    </section>
  );
}

/** Tracker row 6 — where a classification setting's value came from, in words. */
function settingSourceLabel(source: SettingSourceName): string {
  switch (source) {
    case 'submission':
      return 'the submission’s own setting';
    case 'user':
      return 'your user setting';
    case 'org':
      return 'the organisation setting';
    case 'default':
      return 'the default (nobody set it)';
    case 'unavailable':
      return 'nowhere: the lookup failed, so it counts as off';
  }
}

/**
 * Tracker row 6 — the structured classifier-test answer. The headline names the
 * layer that decides and its answer: Layer 2's, or the default classifier's
 * (Layer 3 is run, never shown as a blank). "In production" is shown only when
 * the inputs were production's (`basis` is not 'assumed').
 */
function ClassifierTestResult({ result }: { result: PromptClassifierTestResult }): ReactElement {
  const pct = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : `${Math.round(n * 100)}%`);
  const l2 = result.layer2;
  const l3 = result.layer3;
  const headline =
    l2?.status === 'matched'
      ? `Layer 2 (Prompt Library): ${result.classification ?? '—'} at ${pct(result.confidence)}`
      : l3.status === 'ran'
        ? `Default classifier (Layer 3): ${l3.classification} at ${pct(l3.confidence)}`
        : `Default classifier (Layer 3) decides, but its call failed${l3.status === 'failed' && l3.error ? `: ${l3.error}` : ''}. No result.`;
  const plSetting = result.settings.promptLibraryClassificationEnabled;
  const learningSetting = result.settings.learningFromCorrections;
  const whyNotLayer2 =
    l2?.status === 'matched'
      ? null
      : !result.promptLibraryEnabled
        ? `Prompt Library classification is off (from ${settingSourceLabel(plSetting.source)}), so no Classification prompt was tried.`
        : `No Classification prompt decided${l2?.reason ? ` (${l2.reason})` : ''}.`;
  const l1 = result.layer1;
  const layer1Text =
    l1.status === 'proposal'
      ? `Layer 1 rule proposes ${l1.section ?? '—'} (filename pattern "${l1.pattern}", ${l1.supportCount ?? '—'} confirmed matches). Advisory only — it does not set the section.`
      : l1.status === 'ambiguous'
        ? `Layer 1: pattern "${l1.pattern}" is ambiguous between ${(l1.candidates ?? []).map((c) => c.section).join(', ')} — sent to human review, nothing proposed.`
        : l1.status === 'error'
          ? `Layer 1 could not run: ${l1.error}`
          : `Layer 1: no rule for filename pattern "${l1.pattern}".`;
  return (
    <div
      data-testid="prompt-classifier-test-result"
      style={{ marginTop: 10, padding: 10, background: 'var(--surface2, #f3f4f6)', borderRadius: 6, fontSize: 12, lineHeight: 1.5 }}
    >
      <div data-testid="prompt-classifier-test-headline" style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
        {headline}
      </div>
      {whyNotLayer2 && <div data-testid="prompt-classifier-test-why-not-layer2">{whyNotLayer2}</div>}
      {l2?.status === 'matched' && l2.selectedPrompt && (
        <div>
          By prompt “{l2.selectedPrompt.title}”
          {l2.selectedPrompt.matchedPatterns.length > 0 ? ` (filename matched ${l2.selectedPrompt.matchedPatterns.join(', ')})` : ''}.
        </div>
      )}
      {l2 && <div data-testid="prompt-classifier-test-why" style={{ color: 'var(--text-muted)' }}>{l2.why}</div>}
      {l2?.status === 'no-match' && l2.modelAnswer && (
        <div>
          The model answered {l2.modelAnswer.classification} at {pct(l2.modelAnswer.confidence)} — too unsure to use.
        </div>
      )}
      {result.rationale && <div style={{ marginTop: 4 }}>Rationale: {result.rationale}</div>}
      <div
        data-testid="prompt-classifier-test-basis"
        style={{ marginTop: 6, color: result.basis === 'assumed' ? 'var(--amber, #92400e)' : undefined }}
      >
        {result.productionWouldRun !== null && (
          <>
            <strong>In production:</strong> {result.productionWouldRun}.{' '}
          </>
        )}
        {result.basisText}
      </div>
      <div data-testid="prompt-classifier-test-settings" style={{ marginTop: 4, color: 'var(--text-muted)' }}>
        Prompt Library classification: {plSetting.value ? 'on' : 'off'} (from {settingSourceLabel(plSetting.source)}) ·
        Learning from corrections: {learningSetting.value ? 'on' : 'off'} (from {settingSourceLabel(learningSetting.source)})
      </div>
      <div data-testid="prompt-classifier-test-layer1" style={{ marginTop: 4 }}>{layer1Text}</div>
      {result.promptUnderTest.evaluatedAsActive && (
        <div style={{ marginTop: 4, color: 'var(--amber, #92400e)' }}>
          This prompt is inactive; it was evaluated as if it were active. Production will not use it until it is activated.
        </div>
      )}
      {l2?.selectedPrompt && !result.promptUnderTest.wasSelected && (
        <div style={{ marginTop: 4, color: 'var(--amber, #92400e)' }}>
          Note: production chose a different prompt (“{l2.selectedPrompt.title}”), not the one you are testing.
        </div>
      )}
      <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
        Region {result.region}
        {result.regionSource === 'assumed' ? ' (assumed from the prompt)' : ''} · Model {result.model} · Text sent: {result.excerpt.sentChars} of {result.excerpt.totalChars} characters
        {result.excerpt.truncated ? ` (cut at ${result.excerpt.maxChars}, as in production)` : ''}
      </div>
    </div>
  );
}

/**
 * Tracker row 8 — what a Layer 1 rule rests on. Confirmed documents and
 * folder-scanned files are counted apart; a scan-only rule says "folder scan",
 * never "confirmed matches" (and only confirmations can make it Active).
 */
function layer1SupportLabel(entry: ClassificationEvidenceEntry): string {
  const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;
  const scanned = entry.scanSupportCount ?? 0;
  if (entry.supportCount > 0) {
    const confirmed = plural(entry.supportCount, 'confirmed match', 'confirmed matches');
    return scanned > 0 ? `${confirmed} · ${plural(scanned, 'scanned file', 'scanned files')}` : confirmed;
  }
  return `folder scan · ${plural(scanned, 'file', 'files')} (not confirmed)`;
}

/** Tracker rows 8–9 — the parsed per-file list a job reports (errors when failed, skips when complete). */
function scanJobFileNotes(job: ScanLearnJob): Array<{ file: string; note: string; kind: 'error' | 'skipped' }> {
  if (!job.Errors) return [];
  try {
    const parsed = JSON.parse(job.Errors) as Array<{ file: string; error?: string; skipped?: string }>;
    return parsed.map((e) => ({ file: e.file, note: e.error ?? e.skipped ?? '', kind: e.error ? 'error' : 'skipped' }));
  } catch {
    return [{ file: '(job)', note: job.Errors, kind: 'error' }];
  }
}

/** Tracker row 8 — what a completed scan applied, from the job's own counts (never a guess). */
function scanCompleteSummary(job: ScanLearnJob): string {
  const n = (v: number | null | undefined): number => v ?? 0;
  const skipped = n(job.FilesSkipped);
  return (
    `Scan complete: ${n(job.PromptsCreated)} prompts created, ${n(job.PromptsUpdated)} updated; ` +
    `${n(job.CompaniesInferred)} companies inferred (pending review); ` +
    `${n(job.RulesAdded)} filename rules added, ${n(job.RulesUpdated)} updated` +
    (skipped > 0 ? `; ${skipped} file${skipped === 1 ? '' : 's'} skipped` : '') +
    '.'
  );
}

/** Polls that may fail in a row (3 s apart) before the dialog stops and says contact was lost. */
const SCAN_POLL_MAX_CONSECUTIVE_FAILURES = 5;

/**
 * Tracker row 11 — the id of the scan THIS browser tab started, so returning
 * to the page re-opens its progress. A hint only: the server's
 * GET /scan-learn/latest is the source of truth (another admin's scan, a new
 * tab, cleared storage all still work). Every access is guarded — storage can
 * be unavailable (private mode, blocked site data).
 */
const SCAN_JOB_HINT_KEY = 'autoectd.promptLibrary.scanJobId';
function readScanJobHint(): string | null {
  try {
    return window.sessionStorage.getItem(SCAN_JOB_HINT_KEY);
  } catch {
    return null;
  }
}
function writeScanJobHint(jobId: string | null): void {
  try {
    if (jobId) window.sessionStorage.setItem(SCAN_JOB_HINT_KEY, jobId);
    else window.sessionStorage.removeItem(SCAN_JOB_HINT_KEY);
  } catch {
    // a hint only — the server still knows the job
  }
}
const sameJobId = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();
const isScanJobRunning = (j: ScanLearnJob): boolean => j.Status === 'queued' || j.Status === 'running';
const isScanJobFinished = (j: ScanLearnJob): boolean => j.Status === 'complete' || j.Status === 'failed';

/** Tracker row 11 — a finished job's outcome has been shown: record it once, drop the hint. */
function acknowledgeScanOutcome(job: ScanLearnJob): void {
  if (sameJobId(readScanJobHint(), job.Id)) writeScanJobHint(null);
  if (job.outcomeAcknowledged) return;
  void v2.scanLearn.acknowledge(job.Id).catch(() => {
    // Not fatal: the outcome may be shown once more on a later visit.
  });
}

/**
 * Phase 8.4 — Scan & Learn modal.
 *
 * Tracker rows 8–9: shows exactly what a completed job applied (and who ran
 * it); a failed job shows its reason, each failed file, and that nothing was
 * applied; polling stops — with a message — when the job ends or when the
 * server cannot be reached for SCAN_POLL_MAX_CONSECUTIVE_FAILURES polls.
 * Tracker row 10: a job the server does not know (404) stops polling at once
 * and says so — it is not a lost connection.
 * Tracker row 11: on opening, the tenant's latest job is looked up; a running
 * one resumes in the progress view, and one that finished unseen shows its
 * outcome once. The job keeps running server-side whether or not the dialog
 * (or the page) is open.
 */
function ScanAndLearnModal({
  onClose,
  onComplete,
  onJobUpdate,
}: {
  onClose: () => void;
  onComplete: (job: ScanLearnJob) => void;
  /** Every job state the dialog learns of, so the page can keep its banner honest. */
  onJobUpdate?: (job: ScanLearnJob) => void;
}): ReactElement {
  const [folderPath, setFolderPath] = useState('');
  const [running, setRunning] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [job, setJob] = useState<ScanLearnJob | null>(null);
  const [lostContact, setLostContact] = useState(false);
  const [jobNotFound, setJobNotFound] = useState(false);
  const [checkingLatest, setCheckingLatest] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const failedPolls = useRef(0);
  const onJobUpdateRef = useRef(onJobUpdate);
  onJobUpdateRef.current = onJobUpdate;

  // Tracker row 11 — resume the tenant's latest job, if it is running or its
  // outcome has not been seen yet.
  useEffect(() => {
    let live = true;
    v2.scanLearn
      .latest()
      .then((latest) => {
        if (!live || !latest) return;
        if (isScanJobRunning(latest)) {
          setJob(latest);
          setJobId(latest.Id);
          setJobStatus(latest.Status);
          setRunning(true);
        } else if (isScanJobFinished(latest) && !latest.outcomeAcknowledged) {
          setJob(latest);
          setJobId(latest.Id);
          setJobStatus(latest.Status);
          acknowledgeScanOutcome(latest);
        }
        onJobUpdateRef.current?.(latest);
      })
      .catch(() => {
        // Nothing to resume is not an error: the form below still starts a scan.
      })
      .finally(() => {
        if (live) setCheckingLatest(false);
      });
    return () => {
      live = false;
    };
  }, []);

  // Poll job status every 3 seconds while running. Stops when the job ends,
  // when the server says the job does not exist, or — with a clear message —
  // when the server cannot be reached.
  useEffect(() => {
    if (!jobId || !running) return;
    const interval = window.setInterval(() => {
      v2.scanLearn
        .getJob(jobId)
        .then((polled) => {
          failedPolls.current = 0;
          setJob(polled);
          setJobStatus(polled.Status);
          onJobUpdateRef.current?.(polled);
          if (isScanJobFinished(polled)) {
            setRunning(false);
            window.clearInterval(interval);
            acknowledgeScanOutcome(polled);
            if (polled.Status === 'complete') onComplete(polled);
          }
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) {
            // Tracker row 10 — the server answered: there is no such job.
            setRunning(false);
            setJobNotFound(true);
            window.clearInterval(interval);
            if (sameJobId(readScanJobHint(), jobId)) writeScanJobHint(null);
            return;
          }
          failedPolls.current += 1;
          if (failedPolls.current >= SCAN_POLL_MAX_CONSECUTIVE_FAILURES) {
            setRunning(false);
            setLostContact(true);
            window.clearInterval(interval);
          }
        });
    }, 3000);
    return () => window.clearInterval(interval);
  }, [jobId, running, onComplete]);
  const filesFound = job?.FilesFound ?? null;
  const filesProcessed = job?.FilesProcessed ?? null;
  const notes = job ? scanJobFileNotes(job) : [];

  const startNewScan = (): void => {
    setJob(null);
    setJobId(null);
    setJobStatus(null);
    setLostContact(false);
    setJobNotFound(false);
    setError(null);
    failedPolls.current = 0;
  };

  const handleRun = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!folderPath.trim()) {
      setError('Enter a folder path.');
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const result = await v2.scanLearn.start({
        source: 'folder',
        folderPath: folderPath.trim(),
      });
      writeScanJobHint(result.jobId);
      setJobId(result.jobId);
      setJobStatus(result.status);
    } catch (err) {
      setError(formatApiError(err, 'Failed to start scan', PROMPT_LIBRARY_ERROR_MESSAGES));
      setRunning(false);
    }
  };

  // phase-movable-dialogs — same shared shell; drag it aside to watch the
  // Layer 2 table fill in while the scan runs.
  return (
    <MovableDialog
      persistKey="promptLibrary.scanLearn"
      title="Scan & Learn"
      ariaLabel="Scan and Learn"
      onClose={onClose}
      closeOnBackdrop
      width="min(560px, 92vw)"
      zIndex={320}
      headerRight={
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'none',
            border: 'none',
            fontSize: 20,
            cursor: 'pointer',
            color: 'var(--text-muted)',
          }}
        >
          ×
        </button>
      }
    >
      <form onSubmit={(e) => void handleRun(e)}>

        {error && (
          <div
            role="alert"
            style={{
              color: 'var(--red)',
              background: 'var(--red-bg, #fee2e2)',
              padding: '8px 12px',
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {/* phase-classification-stage-0-fixes item 4 — honest disclosure.
            "Not imported" was true of storage but silent about transmission:
            an excerpt of every readable document's body is sent to the
            external AI service during a scan. Both facts now stand side by
            side, in plain language, before the admin starts one. */}
        <p style={{ margin: '0 0 10px 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Point to a folder containing eCTD submissions. Documents are{' '}
          <strong>not imported</strong> — no document files or document contents are stored
          by AutoeCTD. Only classification prompts and company profiles are created.
        </p>
        <p
          data-testid="scan-learn-transmission-disclosure"
          style={{
            margin: '0 0 16px 0',
            fontSize: 13,
            color: 'var(--text)',
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '10px 12px',
          }}
        >
          <strong>What the scan reads and sends:</strong> to learn naming and
          classification patterns, the scan reads each document it can open and sends an
          excerpt of its text — up to the first 8,000 characters per document — to
          Anthropic, the external AI service this product uses, for analysis. The
          excerpts are used to generate the draft prompts and company profiles shown for
          your review; they are not kept by AutoeCTD after the scan. Do not scan folders
          you are not comfortable sending excerpts of to that service.
        </p>

        <Field label="Folder path">
          <input
            type="text"
            value={folderPath}
            onChange={(e) => setFolderPath(e.target.value)}
            required
            disabled={running}
            aria-label="eCTD folder path"
            data-testid="scan-learn-folder-input"
            placeholder="Folder inside the scan root"
            style={{
              width: '100%',
              padding: 10,
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'ui-monospace, monospace',
              boxSizing: 'border-box',
            }}
          />
        </Field>

        {jobStatus && (
          <div
            style={{
              padding: '10px 14px',
              background: 'var(--surface-alt, #f9fafb)',
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            <strong>Status:</strong> <span data-testid="scan-learn-status">{jobStatus}</span>
            {filesFound !== null && (
              <span style={{ marginLeft: 16 }}>
                {filesProcessed ?? 0} / {filesFound} files
              </span>
            )}
            {job?.CreatedBy && (
              <span style={{ marginLeft: 16, color: 'var(--text-muted)' }}>started by {job.CreatedBy}</span>
            )}
          </div>
        )}

        {/* Tracker row 8 — exactly what the completed scan applied. */}
        {job?.Status === 'complete' && (
          <div
            data-testid="scan-learn-summary"
            style={{ padding: '10px 14px', background: 'var(--green-bg, #DCFCE7)', borderRadius: 6, marginBottom: 12, fontSize: 13 }}
          >
            {scanCompleteSummary(job)}
            {notes.length > 0 && (
              <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                {notes.map((n) => (
                  <li key={n.file}>
                    <code>{n.file}</code> — skipped: {n.note}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Tracker row 9 — a failed scan says why, names each file, and that nothing was applied. */}
        {job?.Status === 'failed' && (
          <div
            role="alert"
            data-testid="scan-learn-failure"
            style={{ color: 'var(--red)', background: 'var(--red-bg, #fee2e2)', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}
          >
            <strong>Scan failed — nothing was applied.</strong> No prompts, companies or filename rules from this
            scan were saved.
            {job.FailureReason && <div style={{ marginTop: 4 }}>{job.FailureReason}</div>}
            {notes.some((n) => n.kind === 'error') && (
              <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                {notes
                  .filter((n) => n.kind === 'error')
                  .map((n) => (
                    <li key={n.file}>
                      <code>{n.file}</code>: {n.note}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}

        {jobNotFound && (
          <div
            role="alert"
            data-testid="scan-learn-not-found"
            style={{ color: 'var(--red)', background: 'var(--red-bg, #fee2e2)', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}
          >
            Scan job not found — the server has no record of this scan, so the dialog stopped checking it. Start a new
            scan if you still need one.
          </div>
        )}

        {lostContact && (
          <div
            role="alert"
            data-testid="scan-learn-lost"
            style={{ color: 'var(--red)', background: 'var(--red-bg, #fee2e2)', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}
          >
            Lost contact with the server — stopped checking this scan. A scan interrupted by a server restart is
            marked failed and applies nothing. Reopen Scan &amp; Learn later to check, or start the scan again.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {running ? 'Running in background…' : 'Close'}
          </button>
          {!running && jobId && (
            <button
              type="button"
              onClick={startNewScan}
              data-testid="scan-learn-new-btn"
              style={{
                background: 'none',
                color: 'var(--accent)',
                border: '1px solid var(--accent)',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              New scan
            </button>
          )}
          {!running && !jobId && (
            <button
              type="submit"
              disabled={running || checkingLatest}
              data-testid="scan-learn-run-btn"
              style={{
                background: 'var(--accent)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {checkingLatest ? 'Checking for a running scan…' : 'Start scan'}
            </button>
          )}
        </div>
      </form>
    </MovableDialog>
  );
}


// ---------------------------------------------------------------------------
// phase-fix-prompt-library-precision-redesign — UnifiedClassificationTable
//
// 5 columns: Type | Name/Pattern | Scope | Status | Source.
// No Actions column. L2 row actions live inside the Source cell wrapped
// in `.prompt-row-actions` (CSS in index.css makes them visible only on
// row hover). L1 and L3 rows have no actions and no empty cell.
//
// Section-divider rows carry a 3px left border in the layer colour (grey
// for L1, blue for L2, violet for L3) — the only layer-specific colour on
// the page. Data rows alternate white / #F9FAFB and hover to #EFF6FF
// (CSS in index.css).
//
// The Scan & Learn and + Add Prompt buttons live in the Layer 2
// section-divider row (right-aligned) — Scan & Learn populates Layer 2
// specifically, so this is the contextually correct location.
//
// Layer 3's Source cell uses React Router `<Link to="/admin/settings">`
// (NOT `<a href>`) — fix for the Azure 404 bug where the hardcoded
// absolute href navigated outside the SPA shell.
// ---------------------------------------------------------------------------

interface UnifiedClassificationTableProps {
  evidenceEntries: ClassificationEvidenceEntry[];
  layer2Prompts: PromptLibraryEntry[] | null;
  layer2Total: number;
  layer2HasActiveFilter: boolean;
  autoConfirmEnabled: boolean;
  autoConfirmThreshold: number;
  reviewThreshold: number;
  canWrite: boolean;
  canDelete: boolean;
  onEditPrompt: (p: PromptLibraryEntry) => void;
  onDeletePrompt: (p: PromptLibraryEntry) => void;
  onToggleActive: (p: PromptLibraryEntry) => Promise<void>;
  onDuplicate: (p: PromptLibraryEntry) => void;
  onCreatePrompt: () => void;
  onScanLearnOpen: () => void;
  onClearFilters: () => void;
}

const TYPE_PILL_STYLES: Record<string, { bg: string; fg: string }> = {
  'Auto Rule': { bg: '#E5E7EB', fg: '#374151' },
  Classification: { bg: '#DBEAFE', fg: '#1E40AF' },
  Template: { bg: '#D1FAE5', fg: '#065F46' },
  Setting: { bg: '#EDE9FE', fg: '#5B21B6' },
};

const STATUS_PILL_STYLES: Record<string, { bg: string; fg: string }> = {
  Active: { bg: '#D1FAE5', fg: '#065F46' },
  Inactive: { bg: '#F3F4F6', fg: '#6B7280' },
  'Read-only': { bg: '#F3F4F6', fg: '#9CA3AF' },
};

const pillStyle = (bg: string, fg: string): CSSProperties => ({
  display: 'inline-block',
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 10,
  background: bg,
  color: fg,
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

function TypePill({ label }: { label: string }): ReactElement {
  const s = TYPE_PILL_STYLES[label] ?? TYPE_PILL_STYLES['Auto Rule']!;
  return <span style={pillStyle(s.bg, s.fg)}>{label}</span>;
}

function StatusPill({
  label,
  testId,
}: {
  label: 'Active' | 'Inactive' | 'Read-only';
  testId?: string;
}): ReactElement {
  const s = STATUS_PILL_STYLES[label]!;
  return (
    <span data-testid={testId} style={pillStyle(s.bg, s.fg)}>
      {label}
    </span>
  );
}

const SOURCE_COL_WIDTH = 160;
const TYPE_COL_WIDTH = 130;
const SCOPE_COL_WIDTH = 140;
const STATUS_COL_WIDTH = 110;

function UnifiedClassificationTable({
  evidenceEntries,
  layer2Prompts,
  layer2Total,
  layer2HasActiveFilter,
  autoConfirmEnabled,
  autoConfirmThreshold,
  reviewThreshold,
  canWrite,
  canDelete,
  onEditPrompt,
  onDeletePrompt,
  onToggleActive,
  onDuplicate,
  onCreatePrompt,
  onScanLearnOpen,
  onClearFilters,
}: UnifiedClassificationTableProps): ReactElement {
  // phase-classification-stage-1-filename-layer — real evidence rows,
  // strongest support first (the service already sorts by effectiveSupport).

  const thStyle: CSSProperties = {
    padding: '10px 8px',
    fontWeight: 600,
    fontSize: 12,
    textAlign: 'left',
    color: '#FFFFFF',
    whiteSpace: 'nowrap',
    background: '#1B3A6B',
  };

  const dividerCellStyle = (borderColor: string): CSSProperties => ({
    padding: '10px 12px',
    background: '#F9FAFB',
    borderLeft: `3px solid ${borderColor}`,
    fontSize: 12,
    fontWeight: 600,
  });

  const scopeOf = (entry: PromptLibraryEntry): string => {
    const mod = entry.Module !== null ? `m${entry.Module}` : '';
    const sect = entry.Section ?? '';
    if (mod && sect) return `${mod}/${sect}`;
    return mod || sect || '—';
  };

  const renderLayer2Row = (entry: PromptLibraryEntry, isOdd: boolean): ReactElement => {
    // phase-ui-review-F14 — the badge, toggle icon and toggle label all derive
    // from this one value, read through the shared runtime-authoritative
    // evaluator rather than the legacy Status column.
    const status: 'Active' | 'Inactive' = isPromptActive(entry) ? 'Active' : 'Inactive';
    return (
      <tr
        key={entry.Id}
        data-testid={`prompt-row-${entry.Id}`}
        className="prompt-row"
        style={{
          background: isOdd ? '#FFFFFF' : '#F9FAFB',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <td style={{ padding: '10px 8px', width: TYPE_COL_WIDTH }}>
          {/* phase-prompt-library-honest-controls item 5 — the pill was
              hard-coded "Classification", so Template-kind prompts (e.g. the
              seeded cover-letter generator) wore the wrong badge. Derive it
              from the row's actual kind. */}
          <TypePill label={entry.PromptType === 'Classification' ? 'Classification' : 'Template'} />
        </td>
        <td style={{ padding: '10px 8px' }}>
          <div style={{ fontWeight: 500 }}>{entry.Title}</div>
          {(entry.Version ?? 1) > 1 && (
            <span style={{ fontSize: 11, color: '#6B7280' }}>v{entry.Version}</span>
          )}
        </td>
        <td style={{ padding: '10px 8px', width: SCOPE_COL_WIDTH, fontSize: 12, color: '#6B7280' }}>
          {scopeOf(entry)}
        </td>
        <td style={{ padding: '10px 8px', width: STATUS_COL_WIDTH }}>
          <StatusPill label={status} testId={`prompt-status-badge-${isPromptActive(entry) ? 'active' : 'draft'}`} />
        </td>
        <td
          style={{
            padding: '10px 8px',
            width: SOURCE_COL_WIDTH,
            fontSize: 12,
            color: '#6B7280',
            position: 'relative',
          }}
        >
          <span>{entry.GeneratingCompany ?? 'Manual'}</span>
          <span
            className="prompt-row-actions"
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: 4,
              background: 'inherit',
              paddingLeft: 6,
            }}
          >
            {/* Tracker row 14 — Activate/Deactivate is a write action
                (PATCH /prompts/:id/status requires Prompts.write). It was
                offered to read-only users, who could only get a 403; it now
                follows the same rule as Edit and Duplicate. The status itself
                stays visible in the pill. */}
            {canWrite && (
              <button
                type="button"
                data-testid={`prompt-toggle-${entry.Id}`}
                onClick={() => void onToggleActive(entry)}
                title={status === 'Active' ? 'Deactivate' : 'Activate'}
                aria-label={status === 'Active' ? 'Deactivate' : 'Activate'}
                style={iconBtnStyle}
              >
                {status === 'Active' ? '⏺' : '▶'}
              </button>
            )}
            {canWrite && (
              <button
                type="button"
                data-testid={`prompt-edit-btn-${entry.Id}`}
                onClick={() => onEditPrompt(entry)}
                title="Edit"
                aria-label="Edit"
                style={iconBtnStyle}
              >
                ✏️
              </button>
            )}
            {canWrite && (
              <button
                type="button"
                data-testid={`prompt-duplicate-btn-${entry.Id}`}
                onClick={() => onDuplicate(entry)}
                title="Duplicate"
                aria-label="Duplicate"
                style={iconBtnStyle}
              >
                ⧉
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                data-testid={`prompt-delete-btn-${entry.Id}`}
                onClick={() => onDeletePrompt(entry)}
                title="Delete"
                aria-label="Delete"
                style={{ ...iconBtnStyle, color: '#DC2626' }}
              >
                🗑
              </button>
            )}
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        data-testid="prompts-table"
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
      >
        <thead>
          <tr>
            <th style={{ ...thStyle, width: TYPE_COL_WIDTH }}>Type</th>
            <th style={thStyle}>Name / Pattern</th>
            <th style={{ ...thStyle, width: SCOPE_COL_WIDTH }}>Scope</th>
            <th style={{ ...thStyle, width: STATUS_COL_WIDTH }}>Status</th>
            <th style={{ ...thStyle, width: SOURCE_COL_WIDTH }}>Source</th>
          </tr>
        </thead>
        <tbody>
          {/* ── Layer 1 divider ── */}
          <tr data-testid="layer1-divider">
            <td colSpan={5} style={dividerCellStyle('#9CA3AF')}>
              <span style={{ marginRight: 8 }}>⚡ Layer 1 — Automatic rules</span>
              <span style={{ fontWeight: 400, color: '#6B7280' }}>
                {/* phase-classification-stage-1-filename-layer — this is now
                    literally true: every row below is derived, never
                    hand-entered, from confirmed classifications and
                    structure-only folder scans. No AI call. */}
                Patterns learned from confirmed classifications and folder-structure scans. No
                AI call.
              </span>
            </td>
          </tr>

          {evidenceEntries.length === 0 ? (
            <tr style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
              <td
                colSpan={5}
                style={{
                  padding: '10px 8px',
                  color: '#6B7280',
                  fontSize: 12,
                  fontStyle: 'italic',
                }}
              >
                No filename patterns learned yet. They accumulate automatically — every
                classification a human confirms, and every folder scan, contributes evidence.
                A pattern needs several confirmed examples before it's strong enough to suggest
                a classification on upload.
              </td>
            </tr>
          ) : (
            evidenceEntries.map((entry, i) => (
              <tr
                key={`ev-${entry.familyCode}-${entry.section}-${entry.pattern}`}
                data-testid="layer1-evidence-row"
                style={{
                  background: i % 2 === 0 ? '#FFFFFF' : '#F9FAFB',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                <td style={{ padding: '10px 8px', width: TYPE_COL_WIDTH }}>
                  <TypePill label="Auto Rule" />
                </td>
                <td style={{ padding: '10px 8px', fontSize: 12 }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace' }}>{entry.pattern}</span>
                  <span style={{ color: '#6B7280' }}> — {entry.familyDisplayName}</span>
                  {/* Tracker row 8 — confirmed documents and folder-scanned files are
                      counted apart; only confirmations make a rule Active. */}
                  <span data-testid="layer1-evidence-support" style={{ color: '#6B7280' }}>
                    {' '}
                    · {layer1SupportLabel(entry)}
                  </span>
                </td>
                <td style={{ padding: '10px 8px', width: SCOPE_COL_WIDTH, fontSize: 12, color: '#6B7280' }}>
                  {entry.section}
                </td>
                <td style={{ padding: '10px 8px', width: STATUS_COL_WIDTH }}>
                  <StatusPill
                    label={entry.readyToPropose ? 'Active' : 'Inactive'}
                    testId={`layer1-evidence-status-${entry.readyToPropose ? 'ready' : 'learning'}`}
                  />
                </td>
                <td style={{ padding: '10px 8px', width: SOURCE_COL_WIDTH, fontSize: 12, color: '#6B7280' }}>
                  {entry.supportCount > 0 ? 'Confirmed history' : 'Folder scan'}
                </td>
              </tr>
            ))
          )}

          {/* ── Layer 2 divider with Scan & Learn + Add Prompt ── */}
          <tr data-testid="layer2-divider">
            <td colSpan={5} style={dividerCellStyle('#3B82F6')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ marginRight: 4 }}>🔍 Layer 2 — Custom prompts</span>
                <span style={{ fontWeight: 400, color: '#6B7280' }}>
                  Your trained prompts by generating company.
                </span>
                {layer2HasActiveFilter && (
                  <span
                    data-testid="layer2-filtered-note"
                    style={{ fontWeight: 400, color: '#6B7280', fontStyle: 'italic' }}
                  >
                    (filtered)
                  </span>
                )}
                {canWrite && (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      data-testid="prompts-scan-learn-btn"
                      onClick={onScanLearnOpen}
                      style={dividerBtnStyle}
                    >
                      🔎 Scan & Learn
                    </button>
                    <button
                      type="button"
                      data-testid="layer2-add-prompt-btn"
                      onClick={onCreatePrompt}
                      style={{
                        ...dividerBtnStyle,
                        background: '#2563EB',
                        color: '#FFFFFF',
                        borderColor: '#2563EB',
                      }}
                    >
                      + Add Prompt
                    </button>
                  </span>
                )}
              </div>
            </td>
          </tr>

          {layer2Prompts === null ? (
            <tr style={{ background: '#FFFFFF' }}>
              <td colSpan={5} style={{ padding: '20px 8px', textAlign: 'center' }}>
                <Spinner caption="" />
              </td>
            </tr>
          ) : layer2Prompts.length === 0 && layer2HasActiveFilter ? (
            <tr data-testid="prompts-empty-row" style={{ background: '#FFFFFF' }}>
              <td colSpan={5} style={{ padding: '20px 8px', textAlign: 'center', color: '#6B7280' }}>
                <span style={{ marginRight: 12 }}>
                  No prompts match the current filters.
                  {layer2Total > 0 && ` ${layer2Prompts.length} of ${layer2Total}.`}
                </span>
                <button
                  type="button"
                  data-testid="layer2-clear-filters-btn"
                  onClick={onClearFilters}
                  style={{
                    background: 'none',
                    border: '1px solid #2563EB',
                    color: '#2563EB',
                    borderRadius: 4,
                    padding: '4px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Clear filters
                </button>
              </td>
            </tr>
          ) : layer2Prompts.length === 0 ? (
            <tr data-testid="layer2-empty-state" style={{ background: '#FFFFFF' }}>
              <td colSpan={5} style={{ padding: '32px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }} aria-hidden="true">
                  📂
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                  No custom prompts yet
                </div>
                <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
                  Your trained prompts will appear here once you run Scan &amp; Learn.
                </div>
                <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
                  Scan &amp; Learn reads your existing eCTD submissions and builds a prompt
                  library automatically.
                </div>
                {canWrite && (
                  <button
                    type="button"
                    data-testid="layer2-empty-state-scan-btn"
                    onClick={onScanLearnOpen}
                    style={{
                      background: '#2563EB',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: 6,
                      padding: '8px 16px',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    🔎 Run Scan &amp; Learn
                  </button>
                )}
              </td>
            </tr>
          ) : (
            (() => {
              const groups = new Map<string, PromptLibraryEntry[]>();
              for (const entry of layer2Prompts) {
                const k = entry.GeneratingCompany?.trim() || 'Unassigned';
                const list = groups.get(k);
                if (list) list.push(entry);
                else groups.set(k, [entry]);
              }
              const sortedKeys = Array.from(groups.keys()).sort((a, b) =>
                a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b),
              );
              const rows: ReactElement[] = [];
              let parity = 0;
              for (const company of sortedKeys) {
                const list = groups.get(company)!;
                rows.push(
                  <tr
                    key={`l2-company-${company}`}
                    data-testid={`layer2-company-subheader-${company}`}
                  >
                    <td colSpan={5} style={{ padding: '6px 8px' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontStyle: 'italic',
                          fontSize: '0.85em',
                          color: '#6B7280',
                        }}
                      >
                        <span>{company}</span>
                        <span>{list.length} prompt{list.length === 1 ? '' : 's'}</span>
                      </div>
                    </td>
                  </tr>,
                );
                for (const entry of list) {
                  rows.push(renderLayer2Row(entry, parity % 2 === 0));
                  parity++;
                }
              }
              return rows;
            })()
          )}

          {/* ── Layer 3 divider ── */}
          <tr data-testid="layer3-divider">
            <td colSpan={5} style={dividerCellStyle('#8B5CF6')}>
              <span style={{ marginRight: 8 }}>🤖 Layer 3 — Generic AI</span>
              <span style={{ fontWeight: 400, color: '#6B7280' }}>
                Fallback thresholds. Applied when no custom prompt matches.
              </span>
            </td>
          </tr>
          <tr
            data-testid="layer3-auto-confirm-row"
            style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}
          >
            <td style={{ padding: '10px 8px', width: TYPE_COL_WIDTH }}>
              <TypePill label="Setting" />
            </td>
            <td style={{ padding: '10px 8px' }}>
              {/* ISS-009 — describe the EFFECTIVE autoConfirmHighConfidence
                  setting: no unconditional automation promise. */}
              {autoConfirmEnabled
                ? `Auto-confirm on — documents classified above ${autoConfirmThreshold}% are accepted automatically`
                : `Auto-confirm off — documents classified above ${autoConfirmThreshold}% are NOT confirmed automatically; they require manual review`}
            </td>
            <td style={{ padding: '10px 8px', width: SCOPE_COL_WIDTH, fontSize: 12, color: '#6B7280' }}>
              —
            </td>
            <td style={{ padding: '10px 8px', width: STATUS_COL_WIDTH }}>
              <StatusPill label="Read-only" />
            </td>
            <td style={{ padding: '10px 8px', width: SOURCE_COL_WIDTH, fontSize: 12 }}>
              <Link
                to="/admin/settings"
                data-testid="layer3-settings-link-auto-confirm"
                style={{ color: '#2563EB', textDecoration: 'none' }}
              >
                → Change in Settings
              </Link>
            </td>
          </tr>
          <tr
            data-testid="layer3-review-queue-row"
            style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}
          >
            <td style={{ padding: '10px 8px', width: TYPE_COL_WIDTH }}>
              <TypePill label="Setting" />
            </td>
            <td style={{ padding: '10px 8px' }}>
              Review queue — documents classified between {reviewThreshold}% and{' '}
              {autoConfirmThreshold}% go to manual review
            </td>
            <td style={{ padding: '10px 8px', width: SCOPE_COL_WIDTH, fontSize: 12, color: '#6B7280' }}>
              —
            </td>
            <td style={{ padding: '10px 8px', width: STATUS_COL_WIDTH }}>
              <StatusPill label="Read-only" />
            </td>
            <td style={{ padding: '10px 8px', width: SOURCE_COL_WIDTH, fontSize: 12 }}>
              <Link
                to="/admin/settings"
                data-testid="layer3-settings-link-review-queue"
                style={{ color: '#2563EB', textDecoration: 'none' }}
              >
                → Change in Settings
              </Link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const iconBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '2px 6px',
  fontSize: 14,
  cursor: 'pointer',
  borderRadius: 4,
  lineHeight: 1,
};

const dividerBtnStyle: CSSProperties = {
  background: '#FFFFFF',
  border: '1px solid #D1D5DB',
  borderRadius: 4,
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  color: '#374151',
};

// ---------------------------------------------------------------------------
// Phase 8.4 — PendingCompaniesPanel (tracker row 12: every status, edit, and
// what confirming / rejecting does to the company's prompts)
// ---------------------------------------------------------------------------

import { forwardRef } from 'react';

type CompanyFilter = 'pending' | 'confirmed' | 'rejected';
const COMPANY_PANEL_PAGE_SIZE = 200;

/** Tracker row 12 — what a review decision will do to the company's prompts, said before it is done. */
function companyDecisionText(c: GeneratingCompanyEntry, status: 'confirmed' | 'rejected'): string {
  const total = c.PromptCount ?? 0;
  const active = c.ActivePromptCount ?? 0;
  const plural = (n: number): string => `${n} Classification prompt${n === 1 ? '' : 's'}`;
  if (status === 'confirmed') {
    return `Confirm "${c.Name}"? ${plural(total - active)} for this company will be activated and used for classification (${active} already active).`;
  }
  return `Reject "${c.Name}"? ${plural(active)} for this company will be set back to draft and stop being used for classification. They are kept, not deleted, and confirming the company later activates them again.`;
}

const PendingCompaniesPanel = forwardRef<
  HTMLElement,
  { onCompaniesReviewed: () => void }
>(function PendingCompaniesPanel({ onCompaniesReviewed }, ref): ReactElement | null {
  const [companies, setCompanies] = useState<GeneratingCompanyEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [loadKey, setLoadKey] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState<CompanyFilter>('pending');
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [decision, setDecision] = useState<{ company: GeneratingCompanyEntry; status: 'confirmed' | 'rejected' } | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string; location: string } | null>(null);
  const [result, setResult] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let current = true;
    setLoading(true);
    v2.generatingCompanies
      .list({ status: filter, page, pageSize: COMPANY_PANEL_PAGE_SIZE })
      .then((res) => {
        if (!current) return;
        const lastPage = Math.max(1, Math.ceil(res.total / COMPANY_PANEL_PAGE_SIZE));
        if (page > lastPage) { setPage(lastPage); return; }
        setCompanies(res.companies);
        setTotal(res.total);
        if (filter === 'pending') setPendingCount(res.total);
        setLoadError(false);
      })
      .catch(() => {
        if (!current) return;
        // T-16 — a failed load is not "no pending companies"; say so.
        setLoadError(true);
      })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [loadKey, filter, page]);

  if (loadError) {
    return (
      <section
        ref={ref}
        data-testid="pending-companies-panel-error"
        role="alert"
        style={{
          marginTop: 24,
          border: '1px solid #FCA5A5',
          background: '#FEF2F2',
          color: '#991B1B',
          borderRadius: 8,
          padding: '12px 16px',
          fontSize: 13,
        }}
      >
        Could not load pending companies.{' '}
        <button
          type="button"
          onClick={() => setLoadKey((k) => k + 1)}
          style={{
            background: 'none',
            border: '1px solid #FCA5A5',
            color: '#991B1B',
            borderRadius: 4,
            padding: '2px 8px',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </section>
    );
  }

  if (!companies) return null;

  const shown = loading ? [] : companies;

  const apply = async (
    id: string,
    data: { status?: 'confirmed' | 'rejected'; name?: string; location?: string | null },
  ): Promise<boolean> => {
    setReviewingId(id);
    setResult(null);
    try {
      const res = await v2.generatingCompanies.review(id, data);
      setResult({ kind: 'success', text: `${res.company.Name}: ${res.message}` });
      setLoadKey((k) => k + 1);
      onCompaniesReviewed();
      return true;
    } catch (err) {
      // Never silent: a refused rename (409) or any failure is said.
      setResult({ kind: 'error', text: formatApiError(err, 'The change was not saved', PROMPT_LIBRARY_ERROR_MESSAGES) });
      return false;
    } finally {
      setReviewingId(null);
    }
  };

  const actionBtn = (bg: string, fg: string, border: string): CSSProperties => ({
    background: bg,
    color: fg,
    border,
    borderRadius: 4,
    padding: '4px 10px',
    marginLeft: 6,
    cursor: 'pointer',
    fontSize: 12,
  });
  const inputStyle: CSSProperties = {
    width: '100%',
    padding: 6,
    border: '1px solid var(--border)',
    borderRadius: 4,
    fontSize: 13,
    boxSizing: 'border-box',
  };

  return (
    <section
      ref={ref}
      style={{
        marginTop: 24,
        border: '1px solid var(--border)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          background: 'var(--surface-alt, #f9fafb)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
          textAlign: 'left',
        }}
        aria-expanded={expanded}
        data-testid="pending-companies-toggle"
      >
        <span>
          🏭 Generating Companies ({pendingCount} awaiting review)
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 }}>
          {expanded ? '▲ Collapse' : '▼ Expand'}
        </span>
      </button>
      {expanded && (
        <div style={{ padding: 16 }}>
          <p style={{ margin: '0 0 12px 0', fontSize: 13, color: 'var(--text-muted)' }}>
            These companies were inferred from eCTD scans. Confirming a company activates its Classification
            prompts; rejecting it (also after a confirm) sets them back to draft. Prompts are never deleted.
            Filename rules (Layer 1) do not depend on the company.
          </p>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }} role="group" aria-label="Show companies">
            {(['pending', 'confirmed', 'rejected'] as const).map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={filter === f}
                data-testid={`companies-filter-${f}`}
                onClick={() => { setFilter(f); setPage(1); setEditing(null); }}
                style={{
                  ...actionBtn(filter === f ? 'var(--accent)' : 'none', filter === f ? '#fff' : 'var(--text)', '1px solid var(--border)'),
                  marginLeft: 0,
                  textTransform: 'capitalize',
                }}
              >
                {f}{filter === f && !loading ? ` (${total})` : ''}
              </button>
            ))}
          </div>
          {total > COMPANY_PANEL_PAGE_SIZE && (
            <nav aria-label="Company pages" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <button type="button" disabled={loading || page === 1} onClick={() => { setPage((p) => p - 1); setEditing(null); }}>
                Previous companies
              </button>
              <span>Page {page} of {Math.ceil(total / COMPANY_PANEL_PAGE_SIZE)} · {total} {filter} companies</span>
              <button type="button" disabled={loading || page * COMPANY_PANEL_PAGE_SIZE >= total} onClick={() => { setPage((p) => p + 1); setEditing(null); }}>
                Next companies
              </button>
            </nav>
          )}
          {result && (
            <div
              data-testid="companies-result"
              role={result.kind === 'error' ? 'alert' : 'status'}
              style={{
                marginBottom: 12,
                padding: '8px 12px',
                borderRadius: 4,
                fontSize: 13,
                background: result.kind === 'error' ? 'var(--red-bg, #fee2e2)' : 'var(--green-bg, #DCFCE7)',
                color: result.kind === 'error' ? 'var(--red)' : 'var(--text)',
              }}
            >
              {result.text}
            </div>
          )}
          {loading ? <p role="status">Loading companies…</p> : shown.length === 0 ? (
            <p data-testid="companies-empty" style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              No {filter} companies.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ ...tdStyle, textAlign: 'left' }}>Name</th>
                  <th style={{ ...tdStyle, textAlign: 'left' }}>Location</th>
                  <th style={{ ...tdStyle, textAlign: 'left' }}>Confidence</th>
                  <th style={{ ...tdStyle, textAlign: 'left' }}>Prompts</th>
                  <th style={{ ...tdStyle, textAlign: 'left' }}>Source</th>
                  <th style={{ ...tdStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => {
                  const busy = reviewingId === c.Id;
                  const isEditing = editing?.id === c.Id;
                  return (
                    <tr key={c.Id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={tdStyle}>
                        {isEditing ? (
                          <input
                            aria-label="Company name"
                            data-testid={`company-edit-name-${c.Id}`}
                            value={editing.name}
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            style={inputStyle}
                          />
                        ) : (
                          c.Name
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                        {isEditing ? (
                          <input
                            aria-label="Company location"
                            data-testid={`company-edit-location-${c.Id}`}
                            value={editing.location}
                            onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                            style={inputStyle}
                          />
                        ) : (
                          (c.Location ?? '—')
                        )}
                      </td>
                      <td style={tdStyle}>
                        {c.ConfidenceScore !== null ? `${Math.round(c.ConfidenceScore * 100)}%` : '—'}
                      </td>
                      <td style={tdStyle} data-testid={`company-prompts-${c.Id}`}>
                        {c.PromptCount === undefined ? '—' : `${c.ActivePromptCount ?? 0} active of ${c.PromptCount}`}
                      </td>
                      {/* Tracker row 8 — attributable: which scan, run by whom. */}
                      <td style={{ ...tdStyle, color: 'var(--text-muted)' }} data-testid={`pending-company-source-${c.Id}`}>
                        {c.InferredFrom}
                        {c.CreatedBy ? ` · by ${c.CreatedBy}` : ''}
                        {c.ScanLearnJobId ? ` · job ${c.ScanLearnJobId.slice(0, 8)}` : ''}
                        {c.ReviewedBy ? ` · reviewed by ${c.ReviewedBy}` : ''}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap', opacity: busy ? 0.5 : 1 }}>
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              disabled={busy || !editing.name.trim()}
                              data-testid={`company-edit-save-${c.Id}`}
                              onClick={() =>
                                void apply(c.Id, {
                                  name: editing.name.trim(),
                                  location: editing.location.trim() ? editing.location.trim() : null,
                                }).then((ok) => {
                                  if (ok) setEditing(null);
                                })
                              }
                              style={actionBtn('var(--accent)', '#fff', 'none')}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setEditing(null)}
                              style={actionBtn('none', 'var(--text-muted)', '1px solid var(--border)')}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            {c.Status !== 'confirmed' && (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => setDecision({ company: c, status: 'confirmed' })}
                                data-testid={`company-confirm-${c.Id}`}
                                style={actionBtn('var(--green, #16a34a)', '#fff', 'none')}
                              >
                                Confirm
                              </button>
                            )}
                            {c.Status !== 'rejected' && (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => setDecision({ company: c, status: 'rejected' })}
                                data-testid={`company-reject-${c.Id}`}
                                style={actionBtn('none', 'var(--red, #dc2626)', '1px solid var(--red, #dc2626)')}
                              >
                                Reject
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                setResult(null);
                                setEditing({ id: c.Id, name: c.Name, location: c.Location ?? '' });
                              }}
                              data-testid={`company-edit-${c.Id}`}
                              style={actionBtn('none', 'var(--text)', '1px solid var(--border)')}
                            >
                              Edit
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      {decision && (
        <ConfirmModal
          title={decision.status === 'confirmed' ? 'Confirm company' : 'Reject company'}
          message={companyDecisionText(decision.company, decision.status)}
          confirmLabel={decision.status === 'confirmed' ? 'Confirm' : 'Reject'}
          variant={decision.status === 'rejected' ? 'destructive' : 'default'}
          onCancel={() => setDecision(null)}
          onConfirm={() => {
            const d = decision;
            setDecision(null);
            void apply(d.company.Id, { status: d.status });
          }}
        />
      )}
    </section>
  );
});
